// src/store/thunks/leadsActions.js
import { createAsyncThunk } from "@reduxjs/toolkit";
import request from "../../utils/request";

// Fetch all leads
export const fetchLeads = createAsyncThunk(
  'leads/fetchLeads',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await request.get('/leads', { withCredentials: true });
      console.log('🔥 Fetched leads:', data);
      return data.leads || data; // just in case it's not wrapped
    } catch (err) {
      console.error('❌ Error fetching leads:', err.response?.data || err.message);
      return rejectWithValue(err.response?.data || 'Error fetching leads');
    }
  }
);

export const updateLead = createAsyncThunk(
  'leads/updateLead',
  async ({ leadId, updates }) => {
    const res = await request.put(`/leads/${leadId}`, { updates }, { withCredentials: true });
    return res.data.lead;
  }
);

// Add a new email to a lead
export const addEmail = createAsyncThunk(
  "leads/addEmail",
  async ({ leadId, address }, { rejectWithValue }) => {
    try {
      const { data } = await request.post(
        `/leads/${leadId}/emails`,
        { address },
        { withCredentials: true }
      );
      return { leadId, address: data.email };
    } catch (err) {
      return rejectWithValue(err.response.data);
    }
  }
);

// Delete an email from a lead
export const deleteEmail = createAsyncThunk(
  "leads/deleteEmail",
  async ({ leadId, address }, { rejectWithValue }) => {
    try {
      await request.delete(
        `/leads/${leadId}/emails/${encodeURIComponent(address)}`,
        { withCredentials: true }
      );
      return { leadId, address };
    } catch (err) {
      return rejectWithValue(err.response.data);
    }
  }
);

// Add a new person to a lead
export const addPerson = createAsyncThunk(
  "leads/addPerson",
  async ({ leadId, person }, { rejectWithValue }) => {
    try {
      const { data } = await request.post(
        `/leads/${leadId}/people`,
        person,
        { withCredentials: true }
      );
      return { leadId, person: data.person };
    } catch (err) {
      return rejectWithValue(err.response.data);
    }
  }
);

// Delete a person from a lead
export const deletePerson = createAsyncThunk(
  "leads/deletePerson",
  async ({ leadId, email }, { rejectWithValue }) => {
    try {
      await request.delete(
        `/leads/${leadId}/people/${encodeURIComponent(email)}`,
        { withCredentials: true }
      );
      return { leadId, email };
    } catch (err) {
      return rejectWithValue(err.response.data);
    }
  }
);

// Save notes for a lead
export const saveNotes = createAsyncThunk(
  "leads/saveNotes",
  async ({ leadId, notes }, { rejectWithValue }) => {
    try {
      const { data } = await request.post(
        `/leads/${leadId}/notes`,
        { notes },
        { withCredentials: true }
      );
      // data.lead now contains notes & notes_history
      return data.lead;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

// Update the status of a lead
export const updateStatus = createAsyncThunk(
  "leads/updateStatus",
  async ({ leadId, status }, { rejectWithValue }) => {
    try {
      const { data } = await request.patch(
        `/leads/${leadId}/status`,
        { status },
        { withCredentials: true }
      );
      return { leadId, status: data.status };
    } catch (err) {
      return rejectWithValue(err.response.data);
    }
  }
);

// Soft-delete a lead
export const deleteLead = createAsyncThunk(
  "leads/deleteLead",
  async (leadId, { rejectWithValue }) => {
    try {
      await request.delete(`/leads/${leadId}`, { withCredentials: true });
      return leadId;
    } catch (err) {
      return rejectWithValue(err.response.data);
    }
  }
);

// Bulk send templated emails to multiple leads
export const bulkEmailSend = createAsyncThunk(
  "leads/bulkEmailSend",
  async ({ templateId, leadIds }, { rejectWithValue }) => {
    try {
      const { data } = await request.post(
        "/leads/bulk-email",
        { templateId, leadIds },
        { withCredentials: true }
      );
      return { message: data.message };
    } catch (err) {
      return rejectWithValue(err.response.data);
    }
  }
);

// export const sendEmail = createAsyncThunk(
//   "leads/sendEmail",
//   async ({ leadId, to, subject, body }, { rejectWithValue }) => {
//     try {
//       const { data } = await request.post(
//         `/leads/${leadId}/send-email`,
//         { to, subject, body },
//         { withCredentials: true }
//       );
//       return { leadId, sent: true };
//     } catch (err) {
//       return rejectWithValue(err.response?.data || err.message);
//     }
//   }
// );

export const sendEmail = createAsyncThunk(
  'leads/sendEmail',
  async (
    {
      leadId,
      from,
      to,
      body,
      text,
      subject,
      threadId,
      campaignId,
      campaignLeadId,
      queueItemId,
      // Optional overrides: we stitch the signature into `body` client-side,
      // but also pass it separately so a cooperating backend can keep its
      // own formatting. disableAutoSignature asks the backend not to append
      // its own generator on top. unsubscribeFooter is a per-send override.
      signatureHtml,
      disableAutoSignature,
      unsubscribeFooter,
    },
    { rejectWithValue }
  ) => {
    try {
      const payload = {
        from,
        to,
        subject,
        body,
        text,
        leadId,
        threadId,
        campaignId,
        campaignLeadId,
        queueItemId,
      };
      if (threadId) payload.threadId = threadId;
      if (signatureHtml) payload.signatureHtml = signatureHtml;
      if (disableAutoSignature) payload.disableAutoSignature = true;
      if (unsubscribeFooter !== undefined) payload.unsubscribeFooter = unsubscribeFooter;
      const { data } = await request.post(
        `/leads/${leadId}/send-email`,
        payload,
        { withCredentials: true }
      );
      return { leadId, messageRecord: data.messageRecord, activity: data.activity };
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);
